import { StructuredTextGraphQlResponse } from "react-datocms";
import { IPlanObject } from "@/api";
import { useBillingIntervalToggle } from "@/components/pricing/BillingIntervalToggle/useBillingIntervalToggle";
import { SELECT_PLAN_SLUGS } from "@/config";
import { MODULE_KEYS } from "@/modules/config";
import { SelectPlanComparisonLayout } from "@/modules/select-plan-comparison/SelectPlanComparisonLayout";
import { SelectPlanComparisonMobileNav } from "@/modules/select-plan-comparison/SelectPlanComparisonMobileNav";
import { SelectPlan } from "@/modules/select-plan-comparison/types";
import { mergeCMSPlanWithStripePlan } from "@/modules/select-plan-comparison/utils";
import { ImageType } from "@/types";
import { ButtonAppearance } from "@narrative-software/narrative-web-ui";
import { ComparisonPlan } from "./ComparisonPlan";
import { PlanComparisonSection } from "./PlanComparisonSection";

const planComparisionFields = `
  title
  description {
    value
  }
  darkMode
  showBillingIntervalControl
  showSectionIntroDivider
  backgroundImage {
    responsiveImage {
      src
    }
  }
  plans {
    ctaLabel
    ctaDisabled
    ctaAppearance
    showRainbowBar
    customCouponBanner
    showCouponSavings
    stripeCoupon {
      code
      discount
      appliesYearly
      appliesMonthly
    }
    planData {
      id
      planName
      stripePlanSlug
      description {
        value
      }
      features {
        id
        name {
          value
        }
        description
      }
    }
  }
`;

type PlanBlock = {
  ctaLabel: string;
  ctaDisabled?: boolean;
  ctaAppearance: ButtonAppearance;
  showRainbowBar?: boolean;
  customCouponBanner: string;
  showCouponSavings: boolean;
  stripeCoupon?: {
    code: string;
    discount: number;
    appliesYearly: boolean;
    appliesMonthly: boolean;
  };
  planData: SelectPlan;
};

type PlanComparisonFields = {
  _modelApiKey: typeof MODULE_KEYS.selectPlanComparison;
  id: string;
  title: string;
  description: StructuredTextGraphQlResponse;
  darkMode?: boolean;
  showBillingIntervalControl?: boolean;
  showSectionIntroDivider?: boolean;
  backgroundImage?: ImageType;
  plans: PlanBlock[];
};

type PlanComparsionProps = PlanComparisonFields & {
  stripePlans: IPlanObject[];
};

/**
 * Partially reimplemented version of SelectPlanComparison that adds a new data layer to separate plan display params
 * from plan data, and to make it easier to create/configure new pages.
 */
const PlanComparison = ({
  title,
  description,
  showBillingIntervalControl,
  showSectionIntroDivider,
  darkMode,
  backgroundImage,
  plans = [],
  stripePlans = [],
}: PlanComparsionProps) => {
  const [billingInterval, setBillingInterval] = useBillingIntervalToggle();

  const plansWithPriceData = mergeCMSPlanWithStripePlan(plans, stripePlans);

  const isBlackFridayPlanPresent = plansWithPriceData.some((plan) => plan.planData.blackFridayPlan);
  // Switch stand pro plan for Black Friday plan if present and on yearly view
  const plansToDisplay = isBlackFridayPlanPresent
    ? plansWithPriceData.filter(
        ({ planData: plan }) =>
          SELECT_PLAN_SLUGS.allFree.includes(plan.stripePlanSlug) ||
          (billingInterval === "month" ? !plan.blackFridayPlan : plan.blackFridayPlan),
      )
    : plansWithPriceData;

  return (
    <PlanComparisonSection
      title={title}
      description={description}
      showBillingIntervalControl={showBillingIntervalControl}
      showSectionIntroDivider={showSectionIntroDivider}
      darkMode={darkMode}
      backgroundImage={backgroundImage}
      billingInterval={billingInterval}
      setBillingInterval={setBillingInterval}
      planComparison={
        <SelectPlanComparisonLayout>
          {plansToDisplay.map((plan, i) => (
            <ComparisonPlan
              key={plan.planData.stripePlanId}
              position={i}
              plan={plan}
              billingInterval={billingInterval}
            />
          ))}
        </SelectPlanComparisonLayout>
      }
      planComparisonMobileNav={
        <SelectPlanComparisonMobileNav
          plans={plansToDisplay.map(({ planData: { planName: name, stripePlanSlug: slug } }) => ({
            name,
            slug,
          }))}
        />
      }
    />
  );
};
export type { PlanBlock, PlanComparisonFields };
export { planComparisionFields, PlanComparison };
